import {Injectable} from '@angular/core';
import {HttpClient, HttpHeaders} from '@angular/common/http';
import {BijbEvent} from '../../models/bijb-event';
import {ServiceStatus} from '../../models/service-status';
import {Deserialize, Serialize} from 'cerialize';
import {ModuleSubscription} from '../../models/module-subscription';
import {User} from '../../models/user';
import {StorageMap} from '@ngx-pwa/local-storage';
import {environment} from '../../../environments/environment';
import {UpgradeMethod} from '../../models/upgrade-method';
import {Rsvp} from '../../models/rsvp';
import {Foto} from '../../models/foto';
import {ProgrammaOnderdeel} from '../../models/programma-onderdeel';
import {Cadeau} from '../../models/cadeau';
import {Theme} from '../../models/theme';
import {CmDetail} from '../../models/cm-detail';
import {Customer} from '../../models/customer';
import {Group} from '../../models/group';
import {LoginPhrase} from '../../models/login-phrase';
import {SecretPage} from '../../models/secret-page';
import {PaymentData} from '../../models/payment-data';
import {RsvpNotification} from '../../models/rsvp-notification';
import {RsvpQuestion} from '../../models/rsvp-question';
import {RsvpQuestionOption} from '../../models/rsvp-question-option';
import {DiscountCode} from '../../models/discount-code';
import {EnclosureCard} from '../../models/enclosure-card';
import {DressCodePicture} from '../../models/dress-code-picture';
import {DressCode} from '../../models/dress-code';
import {forkJoin, Observable} from 'rxjs';
import {map, mergeMap} from 'rxjs/operators';
import {RsvpQuestionTransformer} from '../../helpers/subtypes/rsvp-question-transformer';
import {ChecklistItem} from '../../models/checklist-item';
import {BijbAccount} from '../../models/bijb-account';
import {Invitation} from '../../models/invitation';
import {Chat} from '../../models/chat';
import {Notificatie} from '../../models/notificatie';
import {Role} from '../../models/role';
import { firstValueFrom } from 'rxjs';
import { Background } from '../../models/background';
import { BackgroundType } from '../../models/background-type';

@Injectable({
  providedIn: 'root'
})
export class ApiService {
  public endpoint = environment.apiEndpoint;

  private eventEndpoint(event: BijbEvent): string {
    return this.eventIdEndpoint(event.id);
  }

  private eventIdEndpoint(eventId: number): string {
    return this.endpoint + '/events/' + eventId;
  }

  public constructor(public http: HttpClient, private storage: StorageMap) {
  }

  public getServiceStatus(): Promise<ServiceStatus> {
    return this.http.get(this.endpoint + '/service_status').toPromise()
      .then((data: {}) => Deserialize(data, ServiceStatus));
  }

  public getCurrentUser(event: BijbEvent): Promise<User> {
    return this.doAuthorizedRequest((headers) => this.http.get(this.eventEndpoint(event) + '/user', {
      headers: headers,
      params: {v: '1'}
    }))
      .then((userData: {}) => {
        return Deserialize(userData, User);
      });
  }

  public getCurrentAccount(): Promise<BijbAccount> {
    return this.doAuthorizedRequest((headers) => this.http.get(this.endpoint + '/account', {
      headers: headers,
      params: {v: '1'}
    }))
      .then((userData: {}) => {
        return Deserialize(userData, BijbAccount);
      });
  }

  public updateAccount(account: BijbAccount): Promise<void> {
    const body = {account: Serialize(account)};
    return this.doAuthorizedRequest(
      (headers) => this.http.put(this.endpoint + '/account', body, {
        headers: headers,
        params: {v: '1'}
    }));
  }

  public addEvent(eventName: string, affiliate: string): Promise<BijbEvent> {
    const body = {event: { event_name: eventName, affiliate }};
    return this.doAuthorizedRequest((headers) => this.http.post(this.endpoint + '/events', body, {
      headers: headers,
      params: {v: '1'}
    }))
      .then((eventData: {}) => {
        return Deserialize(eventData, BijbEvent);
      });
  }

  public addFirstEvent(eventName: string, token: { jwt: string }, affiliate: string): Promise<void> {
    const body = {event: { event_name: eventName, affiliate }};
    const headers = new HttpHeaders({Authorization: 'Bearer ' + token.jwt});
    return new Promise<void>((resolve, reject) => {
      this.http.post(this.endpoint + '/events', body, {
        headers: headers,
        params: {v: '1'}
      }).subscribe(() => {
        resolve();
      }, err => {
        reject(err);
      });
    });
  }

  public getEvents(): Promise<BijbEvent[]> {
    return this.doAuthorizedRequest((headers) => this.http.get(this.endpoint + '/events', {
      headers: headers,
      params: {v: '1'}
    }))
      .then((eventsData: {}[]) => {
        return eventsData.map(eventData => Deserialize(eventData, BijbEvent));
      });
  }

  public getEvent(event: BijbEvent): Promise<BijbEvent> {
    return this.doAuthorizedRequest((headers) => this.http.get(this.eventEndpoint(event), {
      headers: headers,
      params: {v: '1', include_payment_info: true}
    }))
      .then((eventData: {}) => {
        return Deserialize(eventData, BijbEvent);
      });
  }

  public updateEvent(event: BijbEvent): Promise<BijbEvent> {
    const body = {event: Serialize(event)};
    return this.doAuthorizedRequest((headers) => this.http.put(this.eventEndpoint(event), body, {
      headers: headers,
      params: {v: '1'}
    })).then((eventData: {}) => {
      return Deserialize(eventData, BijbEvent);
    });
  }

  public getUsers(event: BijbEvent): Promise<User[]> {
    return this.doAuthorizedRequest((headers) => this.http.get(this.eventEndpoint(event) + '/users', {
      headers: headers,
      params: {v: '1'}
    }))
      .then((usersData: {}[]) => {
        return usersData.map(userData => Deserialize(userData, User));
      });
  }

  public updateUser(user: User): Promise<void> {
    const body = {user: Serialize(user)};
    return this.doAuthorizedRequest((headers) => this.http.put(this.endpoint + '/users/' + user.id, body, {
      headers: headers,
      params: {v: '1'}
    }));
  }

  public updateUsers(users: User[], role: Role): Promise<void> {
    const body = {users: Serialize(users, User)}
    return this.doAuthorizedRequest((headers) => this.http.post(this.endpoint + '/users/roles/' + Role[role].toLowerCase(), body, {
      headers: headers,
      params: {v: '1'}
    }));
  }

  public createCustomer(customer: Customer): Promise<void> {
    return this.doAuthorizedRequest((headers) => this.http.post(this.endpoint + '/users/' + customer.id + '/customer_info',
      Serialize(customer), {
        headers: headers,
        params: {v: '1'}
      }));
  }

  public getGroups(event: BijbEvent): Promise<Group[]> {
    return this.doAuthorizedRequest((headers) => this.http.get(this.eventEndpoint(event) + '/groups', {
      headers: headers,
      params: {v: '1'}
    }))
      .then((groupsData: {}[]) => {
        return Deserialize(groupsData, Group);
      });
  }

  public getLoginPhrase(group: Group): Promise<LoginPhrase> {
    return this.doAuthorizedRequest((headers) => this.http.get(this.endpoint + '/groups/' + group.id + '/login_phrase', {
      headers: headers,
      params: {v: '1'}
    }))
      .then((loginPhraseData: {}) => {
        return Deserialize(loginPhraseData, LoginPhrase);
      });
  }

  public updateLoginPhrase(groupId: number, loginPhrase: LoginPhrase): Promise<void> {
    const body = {login_phrase: Serialize(loginPhrase)};
    return this.doAuthorizedRequest(
      (headers) => this.http.put(this.endpoint + '/groups/' + groupId + '/login_phrase', body, {
        headers: headers,
        params: {v: '1'}
      }));
  }

  public getSecretPage(group: Group): Promise<SecretPage> {
    return this.doAuthorizedRequest((headers) => this.http.get(this.endpoint + '/groups/' + group.id + '/secret_page', {
      headers: headers,
      params: {v: '1'}
    }))
      .then((secretPageData: {}) => {
        return Deserialize(secretPageData, SecretPage);
      });
  }

  public updateSecretPage(groupId: number, secretPage: SecretPage): Promise<void> {
    const body = {secret_page: Serialize(secretPage)};
    return this.doAuthorizedRequest(
      (headers) => this.http.put(this.endpoint + '/groups/' + groupId + '/secret_page', body, {
        headers: headers,
        params: {v: '1'}
      }));
  }

  public getEnclosureCard(group: Group): Promise<EnclosureCard> {
    return this.doAuthorizedRequest((headers) => this.http.get(this.endpoint + '/groups/' + group.id + '/enclosure_card', {
      headers: headers,
      params: {v: '1'}
    }))
      .then((enclosureCardData: {}) => {
        return Deserialize(enclosureCardData, EnclosureCard);
      });
  }

  public updateEnclosureCard(enclosureCard: EnclosureCard): Promise<void> {
    const body = {enclosure_card: Serialize(enclosureCard)};
    return this.doAuthorizedRequest(
      (headers) => this.http.put(this.endpoint + '/groups/' + enclosureCard.groupId + '/enclosure_card', body, {
        headers: headers,
        params: {v: '1'}
      }));
  }

  public getDressCode(event: BijbEvent): Promise<DressCode> {
    return this.doAuthorizedRequest((headers) => this.http.get(this.eventEndpoint(event) + '/dress_code', {
      headers: headers,
      params: {v: '1'}
    }))
      .then((dressCodeData: {}) => {
        return Deserialize(dressCodeData, DressCode);
      });
  }

  public updateDressCode(dressCode: DressCode): Promise<void> {
    const body = {dress_code: Serialize(dressCode)};
    return this.doAuthorizedRequest(
      (headers) => this.http.put(this.endpoint + '/dress_codes/' + dressCode.id, body, {
        headers: headers,
        params: {v: '1'}
      }));
  }

  public addGroup(event: BijbEvent, group: Group): Promise<void> {
    const body = {group: Serialize(group)};
    return this.doAuthorizedRequest((headers) => this.http.post(this.eventEndpoint(event) + '/groups', body, {
      headers: headers,
      params: {v: '1'}
    }));
  }

  public updateGroup(group: Group): Promise<void> {
    const body = {group: Serialize(group)};
    return this.doAuthorizedRequest((headers) => this.http.put(this.endpoint + '/groups/' + group.id, body, {
      headers: headers,
      params: {v: '1'}
    }));
  }

  public deleteGroup(id: number): Promise<void> {
    return this.doAuthorizedRequest((headers) => this.http.delete(this.endpoint + '/groups/' + id, {
      headers: headers,
      params: {v: '1'}
    }));
  }

  public getRsvps(event: BijbEvent): Promise<Rsvp[]> {
    return this.doAuthorizedRequest((headers) => this.http.get(this.eventEndpoint(event) + '/rsvps', {
      headers: headers,
      params: {v: '1'}
    }))
      .then((rsvpsData: {}[]) => {
        return Deserialize(rsvpsData, Rsvp);
      });
  }

  public getRsvpNotifications(event: BijbEvent): Promise<RsvpNotification[]> {
    return this.doAuthorizedRequest((headers) => this.http.get(this.eventEndpoint(event) + '/rsvp_notifications', {
      headers: headers,
      params: {v: '1'}
    }))
      .then((notifsData: {}[]) => {
        return notifsData.map(notifData => Deserialize(notifData, RsvpNotification));
      });
  }

  public addRsvpQuestion(event: BijbEvent, rsvpQuestion: RsvpQuestion): Promise<RsvpQuestion> {
    const body = {rsvp_question: Serialize(rsvpQuestion)};
    return this.doAuthorizedRequest((headers) => this.http.post(this.eventEndpoint(event) + '/rsvp_questions', body, {
      headers: headers,
      params: {v: '1'}
    })).then(data => {
      return RsvpQuestionTransformer.Deserialize(data);
    });
  }

  public getRsvpQuestions(event: BijbEvent): Promise<RsvpQuestion[]> {
    return this.doAuthorizedRequest((headers) => this.http.get(this.eventEndpoint(event) + '/rsvp_questions', {
      headers: headers,
      params: {v: '2'}
    }))
      .then((rsvpQuestionsData: {}[]) => {
        return rsvpQuestionsData.map(data => RsvpQuestionTransformer.Deserialize(data));
      });
  }

  public editRsvpQuestion(rsvpQuestion: RsvpQuestion): Promise<void> {
    const body = {rsvp_question: Serialize(rsvpQuestion)};
    return this.doAuthorizedRequest((headers) => this.http.put(this.endpoint + '/rsvp_questions/' + rsvpQuestion.id, body, {
      headers: headers,
      params: {v: '1'}
    }));
  }

  public deleteRsvpQuestion(id: number): Promise<void> {
    return this.doAuthorizedRequest((headers) => this.http.delete(this.endpoint + '/rsvp_questions/' + id, {
      headers: headers,
      params: {v: '1'}
    }));
  }

  public addRsvpQuestionOption(rsvpQuestionId: number, rsvpQuestionOption: RsvpQuestionOption): Promise<void> {
    const body = {rsvp_question_option: Serialize(rsvpQuestionOption)};
    return this.doAuthorizedRequest((headers) => this.http.post(this.endpoint + '/rsvp_questions/' +
      rsvpQuestionId + '/rsvp_question_options', body, {
      headers: headers,
      params: {v: '1'}
    }));
  }

  public deleteRsvpQuestionOption(rsvpQuestionOption: RsvpQuestionOption): Promise<void> {
    return this.doAuthorizedRequest((headers) => this.http.delete(this.endpoint + '/rsvp_question_options/' + rsvpQuestionOption.id, {
      headers: headers,
      params: {v: '1'}
    }));
  }

  public getProgrammaOnderdeels(event: BijbEvent): Promise<ProgrammaOnderdeel[]> {
    return this.doAuthorizedRequest((headers) => this.http.get(this.eventEndpoint(event) + '/programma_onderdeels', {
      headers: headers,
      params: {v: '1'}
    }))
      .then((programmaOnderdeelsData: {}[]) => {
        return programmaOnderdeelsData.map(onderdeelData => Deserialize(onderdeelData, ProgrammaOnderdeel));
      });
  }

  public addProgrammaOnderdeel(event: BijbEvent, po: ProgrammaOnderdeel): Promise<void> {
    const body = {programma_onderdeel: Serialize(po)};
    return this.doAuthorizedRequest((headers) => this.http.post(this.eventEndpoint(event) + '/programma_onderdeels', body, {
      headers: headers,
      params: {v: '1'}
    }));
  }

  public editProgrammaOnderdeel(po: ProgrammaOnderdeel): Promise<void> {
    const body = {programma_onderdeel: Serialize(po)};
    return this.doAuthorizedRequest((headers) => this.http.put(this.endpoint + '/programma_onderdeels/' + po.id, body, {
      headers: headers,
      params: {v: '1'}
    }));
  }

  public deleteProgrammaOnderdeel(id: number): Promise<void> {
    return this.doAuthorizedRequest((headers) => this.http.delete(this.endpoint + '/programma_onderdeels/' + id, {
      headers: headers,
      params: {v: '1'}
    }));
  }

  public addCadeau(event: BijbEvent, cadeau: Cadeau): Promise<void> {
    const body = {cadeau: Serialize(cadeau)};
    return this.doAuthorizedRequest((headers) => this.http.post(this.eventEndpoint(event) + '/cadeaus', body, {
      headers: headers,
      params: {v: '1'}
    }));
  }

  public getCadeaus(event: BijbEvent): Promise<Cadeau[]> {
    return this.doAuthorizedRequest((headers) => this.http.get(this.eventEndpoint(event) + '/cadeaus', {
      headers: headers,
      params: {v: '1'}
    }))
      .then((cadeausData: {}[]) => {
        return cadeausData.map(cadeauData => Deserialize(cadeauData, Cadeau));
      });
  }

  public editCadeau(cadeau: Cadeau): Promise<void> {
    const body = {cadeau: Serialize(cadeau)};
    return this.doAuthorizedRequest((headers) => this.http.put(this.endpoint + '/cadeaus/' + cadeau.id, body, {
      headers: headers,
      params: {v: '1'}
    }));
  }

  public deleteCadeau(id: number): Promise<void> {
    return this.doAuthorizedRequest((headers) => this.http.delete(this.endpoint + '/cadeaus/' + id, {
      headers: headers,
      params: {v: '1'}
    }));
  }

  public addDressCodePicture(event: BijbEvent, dressCodePicture: DressCodePicture): Promise<void> {
    const body = {dress_code_picture: Serialize(dressCodePicture)};
    return this.doAuthorizedRequest((headers) => this.http.post(this.eventEndpoint(event) + '/dress_code_pictures', body, {
      headers: headers,
      params: {v: '1'}
    }));
  }

  public getDressCodePictures(event: BijbEvent): Promise<DressCodePicture[]> {
    return this.doAuthorizedRequest((headers) => this.http.get(this.eventEndpoint(event) + '/dress_code_pictures', {
      headers: headers,
      params: {v: '1'}
    }))
      .then((dressCodePictureData: {}[]) => {
        return Deserialize(dressCodePictureData, DressCodePicture);
      });
  }

  public deleteDressCodePicture(id: number): Promise<void> {
    return this.doAuthorizedRequest((headers) => this.http.delete(this.endpoint + '/dress_code_pictures/' + id, {
      headers: headers,
      params: {v: '1'}
    }));
  }

  public addCmDetails(event: BijbEvent, details: CmDetail): Promise<void> {
    const body = {cm_detail: Serialize(details)};
    return this.doAuthorizedRequest((headers) => this.http.post(this.eventEndpoint(event) + '/cm_details', body, {
      headers: headers,
      params: {v: '1'}
    }));
  }

  public getCmDetails(event: BijbEvent): Promise<CmDetail[]> {
    return this.doAuthorizedRequest((headers) => this.http.get(this.eventEndpoint(event) + '/cm_details', {
      headers: headers,
      params: {v: '1'}
    }))
      .then((cmsDetailsData: {}[]) => {
        return cmsDetailsData.map(cmDetailsData => Deserialize(cmDetailsData, CmDetail));
      });
  }

  public updateCmDetails(cmDetails: CmDetail): Promise<void> {
    const body = {cm_detail: Serialize(cmDetails)};
    return this.doAuthorizedRequest((headers) => this.http.put(this.endpoint + '/cm_details/' + cmDetails.id, body, {
      headers: headers,
      params: {v: '1'}
    }));
  }

  public deleteCmDetails(id: number): Promise<void> {
    return this.doAuthorizedRequest((headers) => this.http.delete(this.endpoint + '/cm_details/' + id, {
      headers: headers,
      params: {v: '1'}
    }));
  }

  public getChats(event: BijbEvent, horizon: moment.Moment): Promise<Chat[]> {
    return this.doAuthorizedRequest((headers) => this.http.get(this.eventEndpoint(event) + '/chats', {
      headers: headers,
      params: {horizon: horizon.unix().toString(), limit: '500', v: '1'}
    }))
      .then((chatsData: {}[]) => {
        return chatsData.map(chatData => Deserialize(chatData, Chat));
      });
  }

  public deleteChats(ids: number[]): Promise<void> {
    const arr = ids.map(String);
    return this.doAuthorizedRequest((headers) => this.http.delete(this.endpoint + '/chats/' + arr, {
      headers: headers,
      params: {v: '1'}
    }));
  }

  public getAnnouncements(event: BijbEvent, horizon: moment.Moment): Promise<Notificatie[]> {
    return this.doAuthorizedRequest((headers) => this.http.get(this.eventEndpoint(event) + '/notificaties', {
      headers: headers,
      params: {horizon: horizon.unix().toString(), limit: '500', v: '1'}
    }))
      .then((notificatiesData: {}[]) => {
        return notificatiesData.map(notificatieData => Deserialize(notificatieData, Notificatie));
      });
  }

  public deleteAnnouncements(ids: number[]): Promise<void> {
    const arr = ids.map(String);
    return this.doAuthorizedRequest((headers) => this.http.delete(this.endpoint + '/notificaties/' + arr, {
      headers: headers,
      params: {v: '1'}
    }));
  }

  public demoSignup(accountDetails: {
    firstName: string,
    lastName: string,
    emailAddress: string,
    password: string,
  }): Promise<{ jwt: string }> {
    const body = {
      account: {
        first_name: accountDetails.firstName,
        last_name: accountDetails.lastName,
        email_address: accountDetails.emailAddress,
        password: accountDetails.password
      }
    };
    return new Promise<{ jwt: string }>((resolve, reject) => {
      this.http.post<{ jwt: string }>(this.endpoint + '/accounts', body).subscribe(token => {
        resolve(token);
      }, err => {
        reject(err);
      });
    });
  }

  public login(email: string, password: string): Observable<void> {
    const body = {account: {email_address: email, password: password}};
    return this.http.post<{ jwt: string }>(this.endpoint + '/accounts/login', body)
      .pipe(
        mergeMap(token => this.storage.set('token', token)),
        map(() => {
          return;
        })
      );
  }

  public loginSlideshow(secret: string, password: string): Promise<void> {
    const body = {password: password};
    return this.http.post<{ event_id: number, slideshow_token: string }>(this.endpoint + '/slideshow/' + secret, body).toPromise().then(token => {
      return this.saveSlideshowCredentials(token.event_id, token.slideshow_token);
    }, error => {
      return Promise.reject(error);
    });
  }

  public getPhotos(event: BijbEvent): Promise<Foto[]> {
    return this.doAuthorizedRequest((headers) => this.http.get(this.eventEndpoint(event) + '/fotos', {
      headers: headers,
      params: {v: '1'}
    }))
      .then((fotoData: {}[]) => {
        return Deserialize(fotoData, Foto);
      });
  }

  public deleteFoto(id: number): Promise<void> {
    return this.doAuthorizedRequest((headers) => this.http.delete(this.endpoint + '/fotos/' + id, {
      headers: headers,
      params: {v: '1'}
    }));
  }

  public downloadFotos(event: BijbEvent, page?: number, pageSize?: number): Promise<Blob> {
    return this.doAuthorizedRequest((headers) => this.http.get(this.eventEndpoint(event) + '/fotos/download_archive', {
      headers: headers,
      params: {v: '1', page: page.toString(), page_size: pageSize.toString()},
      responseType: 'arraybuffer'
    }))
      .then((zipData: ArrayBuffer) => {
        return new Blob([zipData], {type: 'application/zip'});
      });
  }

  public updateTheme(theme: Theme): Promise<void> {
    const body = {theme: Serialize(theme)};
    return this.doAuthorizedRequest((headers) => this.http.put(this.endpoint + '/themes/' + theme.id, body, {
      headers: headers,
      params: {v: '1'}
    }));
  }

  public updateBackground(background: Background): Promise<void> {
    const body = {background: Serialize(background)};
    return this.doAuthorizedRequest((headers) => this.http.put(this.endpoint + '/themes/' + background.themeId + '/update_background', body, {
      headers: headers,
      params: {v: '1'}
    }));
  }

  public getTheme(event: BijbEvent): Promise<Theme> {
    return this.doAuthorizedRequest((headers) => this.http.get(this.eventEndpoint(event) + '/theme', {
      headers: headers,
      params: {v: '1'}
    }))
      .then((themeData: {}) => {
        return Deserialize(themeData, Theme);
      });
  }

  public getSlideshowFotos(secret: string): Promise<Foto[]> {
    return this.loadSlideshowCredentials().then(token => {
      return this.http.get(this.eventIdEndpoint(token.eventId) + '/slideshow/' + secret + '/fotos/' + token.token).toPromise().then((fotosData: {}[]) => {
        return fotosData.map(fotoData => Deserialize(fotoData, Foto));
      });
    });
  }

  public logout(): Promise<void> {
    return new Promise<void>(resolve => {
      this.storage.delete('token').subscribe(() => {
        resolve();
      });
    });
  }

  public recoverAccount(emailAddress: string): Promise<void> {
    const body = {email_address: emailAddress};
    return this.http.post<void>(this.endpoint + '/accounts/recover_account', body).toPromise();
  }

  public recoverAccountChangePassword(secret: string, password: string): Promise<void> {
    const body = {secret: secret, password: password};
    return new Promise<void>((resolve, reject) => {
      this.http.post(this.endpoint + '/accounts/recover_account_change_password', body).subscribe(() => {
        resolve();
      }, error => {
        reject(error);
      });
    });
  }

  public changePassword(currentPassword: string, newPassword: string): Promise<any> {
    const body = {old_password: currentPassword, new_password: newPassword};
    return this.doAuthorizedRequest((headers) => this.http.post(this.endpoint + '/accounts/change_password', body, {
      headers: headers,
      params: {v: '1'}
    }));
  }

  public verifyEmail(secret: string): Promise<void> {
    const body = {secret: secret};
    return new Promise<void>((resolve, reject) => {
      this.http.post(this.endpoint + '/accounts/verify_email', body).subscribe(() => {
        resolve();
      }, error => {
        reject(error);
      });
    });
  }

  public getInvites(event: BijbEvent): Promise<Invitation[]> {
    return this.doAuthorizedRequest((headers) => this.http.get(this.eventEndpoint(event) + '/invitations', {
      headers: headers,
      params: {v: '1'}
    }))
      .then((invitesData: {}[]) => {
        return invitesData.map(invite => Deserialize(invite, Invitation));
      });
  }

  public inviteUser(event: BijbEvent, invitation: Invitation): Promise<void> {
    const body = {invitation: Serialize(invitation)};
    return this.doAuthorizedRequest((headers) => this.http.post(this.eventEndpoint(event) + '/invitations', body, {
      headers: headers,
      params: {v: '1'}
    }));
  }

  public resendInvite(invitation: Invitation): Promise<void> {
    return this.doAuthorizedRequest((headers) => this.http.post(this.endpoint + '/invitations/' + invitation.id + '/resend', {}, {
      headers: headers,
      params: {v: '1'}
    }));
  }

  public deleteInvite(id: number): Promise<void> {
    return this.doAuthorizedRequest((headers) => this.http.delete(this.endpoint + '/invitations/' + id, {
      headers: headers,
      params: {v: '1'}
    }));
  }

  public acceptInviteSetPassword(invitationId: number, secret: string, password: string): Promise<void> {
    const body = {secret: secret, password: password};
    return new Promise<void>((resolve, reject) => {
      this.http.post(this.endpoint + '/invitations/' + invitationId + '/create_invitee', body).subscribe(() => {
        resolve();
      }, error => {
        reject(error);
      });
    });
  }

  public getInvite(invitationId: number): Promise<Invitation> {
    return this.doAuthorizedRequest((headers) => this.http.get(this.endpoint + '/invitations/' + invitationId + '/show_for_existing_user', {
      headers: headers,
      params: {v: '1'}
    }))
      .then((invitationData: {}[]) => {
        return Deserialize(invitationData, Invitation);
      });
  }

  public acceptInvite(invitationId: number): Promise<void> {
    return this.doAuthorizedRequest((headers) => this.http.post(this.endpoint + '/invitations/' + invitationId + '/accept', {}, {
      headers: headers,
      params: {v: '1'}
    }));
  }

  public getDiscountCodes(event: BijbEvent): Promise<DiscountCode[]> {
    return this.doAuthorizedRequest((headers) => this.http.get(this.eventEndpoint(event) + '/discount_codes', {
      headers: headers,
      params: {v: '1'}
    }))
      .then((discountCodesData: {}[]) => {
        return Deserialize(discountCodesData, DiscountCode);
      });
  }

  public redeemDiscountCode(event: BijbEvent, discountCode: DiscountCode): Promise<DiscountCode> {
    const body = {discount_code: Serialize(discountCode)};
    return this.doAuthorizedRequest((headers) => this.http.post(this.eventEndpoint(event) + '/discount_codes/redeem', body, {
      headers: headers,
      params: {v: '1'}
    }))
      .then((discountCodeData: {}[]) => {
        return Deserialize(discountCodeData, DiscountCode);
      });
  }

  public getChecklistItems(event: BijbEvent): Promise<ChecklistItem[]> {
    return this.doAuthorizedRequest((headers) => this.http.get(this.eventEndpoint(event) + '/checklist_items', {
      headers: headers,
      params: {v: '1'}
    }))
      .then((checklistItemsData: {}[]) => {
        return Deserialize(checklistItemsData, ChecklistItem);
      });
  }

  public addChecklistItem(event: BijbEvent, item: ChecklistItem): Promise<void> {
    const body = {checklist_item: Serialize(item)};
    return this.doAuthorizedRequest((headers) => this.http.post(this.eventEndpoint(event) + '/checklist_items', body, {
      headers: headers,
      params: {v: '1'}
    }));
  }

  public deleteChecklistItem(item: ChecklistItem): Promise<void> {
    return this.doAuthorizedRequest((headers) => this.http.delete(this.endpoint + '/checklist_items/' + item.id, {
      headers: headers,
      params: {v: '1'}
    }));
  }

  public createPaymentIntent(event: BijbEvent, body: PaymentData): Promise<{client_secret?: string, redirect_url?: string}> {
    return this.doAuthorizedRequest((headers) => this.http.post(
      this.eventEndpoint(event) + '/stripe_webhooks', Serialize(body), {
        headers: headers,
        params: {v: '1'}
      }));
  }

  public registerUpgradeMethod(event: BijbEvent, upgradeMethod: UpgradeMethod): Promise<void> {
    const body = {
      upgrade_method: upgradeMethod
    };
    return this.doAuthorizedRequest((headers) => this.http.post(
      this.eventEndpoint(event) + '/register_upgrade_method', body, {
        headers: headers,
        params: {v: '1'}
      }));
  }

  public useFullDiscount(event: BijbEvent): Promise<void> {
    return this.doAuthorizedRequest((headers) => this.http.post(
      this.eventEndpoint(event) + '/use_full_discount', {}, {headers: headers}));
  }

  public getModuleSubscriptions(event: BijbEvent): Promise<ModuleSubscription[]> {
    return this.doAuthorizedRequest((headers) => this.http.get(this.eventEndpoint(event) + '/module_subscriptions', {
      headers: headers,
      params: {v: '1'}
    }))
      .then((moduleSubscriptionsData: {}[]) => {
        return Deserialize(moduleSubscriptionsData, ModuleSubscription);
      });
  }

  public updateModuleSubscription(moduleSubscription: ModuleSubscription): Promise<void> {
    const body = {module_subscription: Serialize(moduleSubscription)};
    return this.doAuthorizedRequest((headers) => this.http.post(this.eventIdEndpoint(moduleSubscription.eventId) + '/module_subscriptions', body, {
      headers: headers,
      params: {v: '1'}
    }));
  }

  public loadToken(): Promise<{ jwt: string }> {
    return new Promise<{ jwt: string }>((resolve, reject) => {
      return this.storage.get('token').subscribe((token: { jwt: string }) => {
        if (token && token.hasOwnProperty('jwt')) {
          resolve(token);
        } else {
          reject('no_valid_token');
        }
      });
    });
  }

  public fetchToken(): Promise<{ jwt: string }> {
    return this.doAuthorizedRequest((headers) => this.http.get(this.endpoint + '/accounts/my_token', {
      headers: headers,
      params: {v: '1'}
    }));
  }

  public saveSlideshowCredentials(eventId: number, token: string): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      forkJoin([
        this.storage.set('slideshowEventId', eventId),
        this.storage.set('slideshowToken', token)
      ]).subscribe({
        next: () => {
          resolve();
        },
        error: (error) => {
          reject(error);
        }
      });
    });
  }

  public loadSlideshowCredentials(): Promise<{ eventId: number, token: string }> {
    return firstValueFrom(forkJoin([
      this.storage.get('slideshowEventId') as Observable<number>,
      this.storage.get('slideshowToken') as Observable<string>
    ]).pipe(map(
        ([eventId, token]) => ({ eventId: eventId, token: token })
      ))
    );
  }

  private doAuthorizedRequest(request: (headers: HttpHeaders) => Observable<Object | ArrayBuffer>): Promise<any> {
    return new Promise((resolve, reject) => {
      this.loadToken().then(token => {
        const headers = new HttpHeaders({ Authorization: 'Bearer ' + token.jwt });

        request(headers).subscribe({
          next: data => resolve(data),
          error: err => reject(err)
        });
      }, message => {
        reject(message);
      });
    });
  }
}
